import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { getBuyerBalanceSummaryByBuyerId } from "selectors/clearingSaleInvoice";
import { Subtitle, Text } from "components/Text";
import { formatDollarsFromCents } from "lib";
import {
  BuyerPaymentSummaryData,
  BuyerPaymentSummaryItem,
} from "constants/payments";
import { Column, Row } from "components/Layout";
import { ModalTableWrapper } from "components/AgGrid/TableWrapper";
import AgGrid from "components/AgGrid/AgGrid";

import { BuyerPaymentSummaryLotColumnDefs } from "constants/columnDefinitions";
import { globalDefaultColDef } from "components/AgGrid/AgGridContainer";

type BreakDownItem = { label: string; amount: string };

// Helper to reduce repetition when format currency values
const formatCurrency = (cents): string =>
  formatDollarsFromCents(cents, { format: true });

const getRowId = (params: { data: BuyerPaymentSummaryItem }) =>
  params.data.lotId;

const itemsTableDefaultColDef = {
  ...globalDefaultColDef,
  filter: false,
  floatingFilter: false,
};

interface PurchaseSummaryTableProps {
  businessId: string;
}

export function PurchaseSummaryTable(props: PurchaseSummaryTableProps) {
  const { businessId } = props;

  const buyerBalanceSummaryLookup = useSelector<State, BuyerPaymentSummaryData>(
    getBuyerBalanceSummaryByBuyerId(businessId),
  );

  const { items, totals } = buyerBalanceSummaryLookup;

  const formattedTotals = useMemo(() => {
    const breakdownItems: BreakDownItem[] = [];

    if (totals.amountExclCents > 0) {
      breakdownItems.push({
        label: "Amount excl",
        amount: formatCurrency(totals.amountExclCents),
      });
    }
    if (totals.feesCents > 0) {
      breakdownItems.push({
        label: "Fees",
        amount: formatCurrency(totals.feesCents),
      });
    }
    if (totals.taxCents > 0) {
      breakdownItems.push({
        label: "Tax",
        amount: formatCurrency(totals.taxCents),
      });
    }

    return {
      due: formatCurrency(totals.dueCents),
      breakdownItems,
    };
  }, [totals]);

  if (!buyerBalanceSummaryLookup.items.length) {
    return <div>No Sale Lots were purchased by this buyer.</div>;
  }

  return (
    <>
      <Column>
        {formattedTotals.breakdownItems.length > 0 && (
          <Row
            marginTop={1}
            marginBottom={2}
            textAlignLeft
            flexWrap
            gridGap={3}
          >
            {formattedTotals.breakdownItems.map(item => (
              <Row key={item.label} whiteSpaceNoWrap gridGap={1}>
                <Text fontSize="beta" color="gray44">
                  {item.label}
                </Text>
                <Text fontSize="beta" color="black">
                  {item.amount}
                </Text>
              </Row>
            ))}
          </Row>
        )}
      </Column>

      <Column>
        <Row justifyBetween alignCenter marginBottom={2} minHeight="60px">
          <Subtitle inline>Items</Subtitle>
        </Row>
        <ModalTableWrapper minHeight={125}>
          <AgGrid
            rowData={items}
            columnDefs={BuyerPaymentSummaryLotColumnDefs}
            defaultColDef={itemsTableDefaultColDef}
            getRowId={getRowId}
            suppressColumnVirtualisation
            suppressScrollOnNewData
          />
        </ModalTableWrapper>
      </Column>
    </>
  );
}
